import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import { MdClose, MdOpenInNew } from "react-icons/md";

const SingleAttachment = ({ file, BASE_URL, fileSize }) => {
  const [previewImage, setPreviewImage] = useState(null);

  if (!file) {
    return (
      <Typography
        sx={{ fontSize: 14, color: "text.secondary", fontStyle: "italic" }}
      >
        No documents attached
      </Typography>
    );
  }

  function getMediaUrl(file) {
    // If it's already a full URL, return it as is
    // if (file.startsWith('http')) return file;

    // Normalize: guarantee the file starts with 'media/'
    const normalizedFile = file.startsWith("/media/")
      ? file
      : file.startsWith("media/")
        ? `/media/${file}`
        : `media/${file}`;

    // Construct the URL using the URL constructor (avoids double-slash issues)
    return `${BASE_URL}${normalizedFile}`;
  }

  // const fileUrl = `${BASE_URL}/media/${file}`;
  const fileUrl = getMediaUrl(file);
  console.log("fileUrl", fileUrl);
  const fileName = file.split("/").pop();
  const extension = fileName.split(".").pop().toLowerCase();
  const isImage = ["jpg", "jpeg", "png", "webp"].includes(extension);

  const handleDownload = async () => {
    const res = await fetch(fileUrl);
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleOpenNewTab = () => {
    if (!fileUrl) return;
    window.open(fileUrl, "_blank", "noopener,noreferrer");
  };

  console.log("fileName", fileName);

  return (
    <>
      {fileName != "default.png" && isImage ? (
        <Paper
          elevation={0}
          sx={{
            width: 220,
            borderRadius: 3,
            border: "1px solid #e5e7eb",
            overflow: "hidden",
            position: "relative",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
            },
            "&:hover .hoverIcons": {
              opacity: 1,
              transform: "translateY(0)",
            },
          }}
        >
          {/* Preview */}
          <Box
            sx={{
              height: 140,
              backgroundColor: "#f8fafc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              borderRadius: 1,
              overflow: "hidden",
              cursor: "pointer",
              "&:hover": {
                "& .media-content": {
                  filter: "brightness(0.6)", // Makes image darker on hover
                },
                "& .hoverIcons": {
                  opacity: 1,
                  transform: "translateY(0)",
                  "& .MuiIconButton-root": {
                    opacity: 1,
                    transform: "scale(1)",
                  },
                },
              },
            }}
          >
            {/* Media Content (Image or Icon) */}
            <Box
              className="media-content"
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "filter 0.3s ease-in-out",
                filter: "brightness(1)", // Normal brightness
              }}
            >
              {isImage && fileName != "default.png" ? (
                <Box
                  component="img"
                  src={fileUrl}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : extension === "pdf" ? (
                <PictureAsPdfRoundedIcon
                  sx={{
                    fontSize: 65,
                    color: "#ef4444",
                  }}
                />
              ) : extension === "xlsx" ||
                extension === "xls" ||
                extension === "csv" ? (
                <InsertDriveFileRoundedIcon
                  sx={{
                    fontSize: 65,
                    color: "#10b981",
                  }}
                />
              ) : (
                <InsertDriveFileRoundedIcon
                  sx={{
                    fontSize: 65,
                    color: "#64748b",
                  }}
                />
              )}
            </Box>

            {/* Colorful Hover Icons - Bright and prominent */}
            <Box
              className="hoverIcons"
              sx={{
                position: "absolute",
                bottom: 14,
                right: 14,
                display: "flex",
                gap: 1.5,
                opacity: 0,
                transform: "translateY(12px)",
                transition: "all 0.3s ease-in-out",
                zIndex: 2,
              }}
            >
              {/* Preview Icon */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  isImage
                    ? setPreviewImage(fileUrl)
                    : window.open(fileUrl, "_blank");
                }}
                sx={{
                  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                  color: "#fff",
                  width: 36,
                  height: 36,
                  opacity: 1,
                  boxShadow: "0 4px 15px rgba(99,102,241,0.5)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.2)",
                    boxShadow: "0 8px 25px rgba(99,102,241,0.8)",
                    background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                  },
                }}
              >
                <VisibilityRoundedIcon fontSize="small" />
              </IconButton>

              {/* Download Icon */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
                sx={{
                  background: "linear-gradient(135deg, #10b981, #06b6d4)",
                  color: "#fff",
                  width: 36,
                  height: 36,
                  opacity: 1,
                  boxShadow: "0 4px 15px rgba(16,185,129,0.5)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.2)",
                    boxShadow: "0 8px 25px rgba(16,185,129,0.8)",
                    background: "linear-gradient(135deg, #059669, #0891b2)",
                  },
                }}
              >
                <DownloadRoundedIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Optional: Semi-transparent overlay for extra darkness */}
            <Box
              className="hoverOverlay"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0)",
                transition: "background-color 0.3s ease",
                pointerEvents: "none",
                zIndex: 1,
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                },
              }}
            />
          </Box>

          {/* File Info */}
          <Box sx={{ p: 1 }}>
            <Tooltip title={fileName}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {fileName}
              </Typography>
            </Tooltip>

            {fileSize && (
              <Typography
                sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}
              >
                {fileSize > 1024
                  ? `${(fileSize / 1024).toFixed(1)} MB`
                  : `${fileSize} KB`}
              </Typography>
            )}
          </Box>
        </Paper>
      ) : (
        <Box>
          <Typography variant="subtitle2">No Attachments</Typography>
        </Box>
      )}

      {/* Lightbox */}
      <Dialog
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            p: 1,
            pb: 0,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <Typography variant="h5">{fileName}</Typography>
          {/* ACTION BUTTONS */}
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "end" }}>
            {/* Open in new tab */}
            <Tooltip title="Open in new tab">
              <IconButton size="small" onClick={handleOpenNewTab}>
                <MdOpenInNew />
              </IconButton>
            </Tooltip>

            {/* Close */}
            <Tooltip title="Close">
              <IconButton size="small" onClick={() => setPreviewImage(null)}>
                <MdClose />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1 }}>
          <Box
            component="img"
            src={previewImage}
            sx={{ width: "100%", maxHeight: "70vh", objectFit: "cover" }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SingleAttachment;
